import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Clock, 
  Play, 
  Square, 
  X, 
  User, 
  Briefcase,
  Check,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { formatCaseName } from '../utils/caseNameFormatter';
import SearchableClientSelect from './SearchableClientSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to format time
const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Helper to remove Greek accents
const removeAccents = (str) => {
  if (!str) return '';
  const accentMap = {
    'Ά': 'Α', 'Έ': 'Ε', 'Ή': 'Η', 'Ί': 'Ι', 'Ό': 'Ο', 'Ύ': 'Υ', 'Ώ': 'Ω',
    'ά': 'Α', 'έ': 'Ε', 'ή': 'Η', 'ί': 'Ι', 'ό': 'Ο', 'ύ': 'Υ', 'ώ': 'Ω',
    'ϊ': 'Ι', 'ϋ': 'Υ', 'ΐ': 'Ι', 'ΰ': 'Υ', 'Ϊ': 'Ι', 'Ϋ': 'Υ'
  };
  return str.toUpperCase().split('').map(char => accentMap[char] || char).join('');
};

const TimeTrackerModal = ({ isOpen, onClose, clients = [], cases = [], entries = [], onTimeEntrySaved }) => {
  // Timer state
  const [isTracking, setIsTracking] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const timerRef = useRef(null);
  
  // Form state
  const [step, setStep] = useState('select'); // 'select', 'tracking', 'save'
  const [trackingType, setTrackingType] = useState(''); // 'case' or 'extrajudicial'
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [description, setDescription] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [addToFinancial, setAddToFinancial] = useState(true);
  const [dayTaskId, setDayTaskId] = useState(null);

  // Check for active timer on mount
  useEffect(() => {
    const savedTimer = localStorage.getItem('courtplan_active_timer');
    if (savedTimer) {
      const timerData = JSON.parse(savedTimer);
      setIsTracking(true);
      setStartTime(new Date(timerData.startTime));
      setTrackingType(timerData.trackingType);
      setSelectedCaseId(timerData.caseId || '');
      setSelectedClientId(timerData.clientId || '');
      setDescription(timerData.description || '');
      setHourlyRate(timerData.hourlyRate || '');
      setDayTaskId(timerData.dayTaskId || null);
      setStep('tracking');
      
      // Calculate elapsed time
      const elapsed = Math.floor((Date.now() - new Date(timerData.startTime).getTime()) / 1000);
      setElapsedSeconds(elapsed);
    }
  }, [isOpen]);

  // Timer tick
  useEffect(() => {
    if (isTracking && startTime) {
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
        setElapsedSeconds(elapsed);
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isTracking, startTime]);

  const startTracking = async () => {
    const now = new Date();
    setStartTime(now);
    setIsTracking(true);
    setElapsedSeconds(0);
    setStep('tracking');

    // Καταχώρηση στο Πρόγραμμα Ημέρας (μη μπλοκάρουσα)
    let createdDayTaskId = null;
    try {
      const ymd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const res = await axios.post(`${API}/day-tasks`, {
        date: ymd,
        title: `Χρονοχρέωση${description ? ` — ${description}` : ''}`,
        start_time: hhmm,
        duration_minutes: 30,
        client_id: selectedClientId || '',
        case_id: selectedCaseId || '',
        source: 'time_tracking',
      });
      createdDayTaskId = res.data?.id || null;
      setDayTaskId(createdDayTaskId);
    } catch (e) {
      console.error('Day planner task creation failed:', e);
    }

    // Save to localStorage for persistence
    const timerData = {
      startTime: now.toISOString(),
      trackingType,
      caseId: selectedCaseId,
      clientId: selectedClientId,
      description,
      hourlyRate,
      dayTaskId: createdDayTaskId
    };
    localStorage.setItem('courtplan_active_timer', JSON.stringify(timerData));
    
    toast.success('Η χρονοχρέωση ξεκίνησε');
  };

  const stopTracking = () => {
    setIsTracking(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setStep('save');
  };

  const cancelTracking = () => {
    setIsTracking(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (dayTaskId) {
      axios.delete(`${API}/day-tasks/${dayTaskId}`).catch(() => {});
      setDayTaskId(null);
    }
    localStorage.removeItem('courtplan_active_timer');
    resetForm();
    toast.info('Η χρονοχρέωση ακυρώθηκε');
  };

  const saveTimeEntry = async () => {
    const hours = elapsedSeconds / 3600;
    const rate = parseFloat(hourlyRate) || 0;
    const amount = hours * rate;

    try {
      // Always create time entry in financial management
      const financialEntry = {
        entry_type: 'fee',
        amount: Math.round(amount * 100) / 100,
        description: description || 'Χρονοχρέωση',
        date: new Date().toISOString().split('T')[0],
        client_id: selectedClientId || null,
        case_id: selectedCaseId || null,
        fee_type: 'hourly',
        hours: Math.round(hours * 100) / 100,
        hourly_rate: rate,
        is_paid: false,
        notes: `Διάρκεια: ${formatTime(elapsedSeconds)}`
      };
      await axios.post(`${API}/financial/entries`, financialEntry);

      // Ενημέρωση καταχώρησης στο Πρόγραμμα Ημέρας με πραγματική διάρκεια
      if (dayTaskId) {
        try {
          await axios.put(`${API}/day-tasks/${dayTaskId}`, {
            duration_minutes: Math.max(5, Math.round(elapsedSeconds / 60)),
            status: 'done',
            title: `Χρονοχρέωση${description ? ` — ${description}` : ''} (${formatTime(elapsedSeconds)})`,
          });
        } catch (e) {
          console.error('Day planner task update failed:', e);
        }
        setDayTaskId(null);
      }

      // NOTE: Previously a secondary case-entry was also created here, which double-charged
      // the user 1 extra credit. Per user request, time-tracking is billed ONLY as a financial
      // entry. The case-entry duplicate has been removed.

      // Clear localStorage
      localStorage.removeItem('courtplan_active_timer');
      
      toast.success(`Η χρονοχρέωση καταχωρήθηκε (${formatTime(elapsedSeconds)})`);
      
      if (onTimeEntrySaved) {
        onTimeEntrySaved();
      }
      
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error saving time entry:', error);
      toast.error('Σφάλμα καταχώρησης');
    }
  };

  const resetForm = () => {
    setStep('select');
    setTrackingType('');
    setSelectedCaseId('');
    setSelectedClientId('');
    setDescription('');
    setHourlyRate('');
    setElapsedSeconds(0);
    setStartTime(null);
    setIsTracking(false);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return '-';
    if (client.client_type === 'natural') {
      return removeAccents(`${client.surname} ${client.name}`.trim().toUpperCase());
    }
    return removeAccents((client.company_name || '-').toUpperCase());
  };

  const getCaseDescription = (caseId) => {
    const caseItem = cases.find(c => c.id === caseId);
    if (!caseItem) return 'Χωρίς περιγραφή';
    return formatCaseName(caseItem, entries);
  };

  // Get cases for selected client
  const clientCases = selectedClientId 
    ? cases.filter(c => c.client_id === selectedClientId)
    : cases;

  // Render based on step
  const renderContent = () => {
    // If tracking is active, show tracking view
    if (step === 'tracking') {
      return (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-stone-200 bg-stone-50">
            <p className="text-sm text-slate-600">
              {trackingType === 'case' ? 'Υπόθεση' : 'Εξωδικαστική εργασία'}
              {selectedCaseId && ` - ${getCaseDescription(selectedCaseId)}`}
            </p>
            {description && (
              <p className="text-sm font-medium text-slate-900 mt-1">{description}</p>
            )}
          </div>
          
          {/* Timer Display */}
          <div className="text-center py-8 px-4">
            <div className="text-5xl font-mono font-bold text-slate-900 mb-4">
              {formatTime(elapsedSeconds)}
            </div>
            
            {/* Pulsing indicator */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-full border border-red-100">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-red-700">Σε εξέλιξη</span>
            </div>
          </div>

          {/* Stop Button */}
          <div className="p-4 border-t border-stone-200 bg-stone-50">
            <button
              data-testid="time-stop-btn"
              onClick={stopTracking}
              className="w-full flex items-center justify-center gap-2 h-11 bg-red-700 hover:bg-red-800 text-white font-medium rounded-xl shadow-lg shadow-red-900/20 transition-all duration-200"
            >
              <Square className="w-4 h-4" />
              Λήξη χρονοχρέωσης
            </button>
          </div>
        </div>
      );
    }

    // Save step
    if (step === 'save') {
      const hours = elapsedSeconds / 3600;
      const rate = parseFloat(hourlyRate) || 0;
      const amount = hours * rate;

      return (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-stone-200">
            <h3 className="flex items-center gap-2.5 font-semibold text-slate-900 tracking-tight">Σύνοψη χρονοχρέωσης</h3>
          </div>
          
          {/* Summary */}
          <div className="p-4 space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-stone-100">
              <span className="text-sm text-slate-600">Διάρκεια</span>
              <span className="font-mono font-bold text-lg text-slate-900">{formatTime(elapsedSeconds)}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-stone-100">
              <span className="text-sm text-slate-600">Ώρες</span>
              <span className="font-medium text-slate-900">{(hours).toFixed(2)}</span>
            </div>
            
            {/* Hourly Rate Input */}
            <div className="pt-2">
              <Label className="text-sm text-slate-600">Τιμή ανά ώρα (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                placeholder="π.χ. 80.00"
                className="mt-1.5 h-10 bg-white border-stone-300"
              />
            </div>

            {rate > 0 && (
              <div className="flex justify-between items-center py-3 px-3 bg-green-50 rounded-lg border border-green-100 mt-3">
                <span className="text-sm font-medium text-green-800">Σύνολο</span>
                <span className="font-bold text-lg text-green-700">{Math.round(amount).toLocaleString('de-DE')} €</span>
              </div>
            )}

            {/* Add to Financial */}
            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="add-to-financial"
                checked={addToFinancial}
                onChange={(e) => setAddToFinancial(e.target.checked)}
                className="h-4 w-4 rounded border-stone-300 accent-red-700"
                style={{ accentColor: '#b91c1c' }}
              />
              <Label htmlFor="add-to-financial" className="text-sm text-slate-600 cursor-pointer">
                Καταχώρηση στην οικονομική διαχείριση
              </Label>
            </div>
          </div>

          {/* Actions */}
          <div className="p-4 border-t border-stone-200 bg-stone-50 flex gap-3">
            <button
              data-testid="time-cancel-btn"
              onClick={cancelTracking}
              className="flex-1 flex items-center justify-center gap-2 h-10 border border-stone-300 text-slate-700 font-medium rounded-xl hover:bg-stone-100 transition-all duration-200"
            >
              <X className="w-4 h-4" />
              Ακύρωση
            </button>
            <button
              data-testid="time-save-btn"
              onClick={saveTimeEntry}
              className="flex-1 flex items-center justify-center gap-2 h-10 bg-red-700 hover:bg-red-800 text-white font-medium rounded-xl shadow-md shadow-red-900/20 transition-all duration-200"
            >
              <Check className="w-4 h-4" />
              Καταχώρηση
            </button>
          </div>
        </div>
      );
    }

    // Select step (default)
    return (
      <div className="space-y-4">
        {/* Tracking Type Selection */}
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-stone-200">
            <h3 className="flex items-center gap-2.5 font-semibold text-slate-900 tracking-tight">Η χρονοχρέωση αφορά</h3>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                data-testid="time-type-case-btn"
                onClick={() => {
                  setTrackingType('case');
                  setSelectedClientId('');
                }}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  trackingType === 'case'
                    ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25'
                    : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                }`}
              >
                Υπόθεση
              </button>
              <button
                data-testid="time-type-extrajudicial-btn"
                onClick={() => {
                  setTrackingType('extrajudicial');
                  setSelectedCaseId('');
                }}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  trackingType === 'extrajudicial'
                    ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25'
                    : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                }`}
              >
                Εξωδικαστική απασχόληση
              </button>
            </div>
          </div>
        </div>

        {/* Combined Form - Στοιχεία χρέωσης */}
        {trackingType && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-stone-200">
              <h3 className="flex items-center gap-2.5 font-semibold text-slate-900 tracking-tight">Στοιχεία χρέωσης</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label className="text-sm text-slate-600">Επιλογή πελάτη</Label>
                <div className="mt-1.5">
                  <SearchableClientSelect
                    value={selectedClientId || ''}
                    onChange={(val) => {
                      setSelectedClientId(val);
                      setSelectedCaseId('');
                    }}
                    clients={clients}
                    placeholder="Χωρίς πελάτη"
                    allowNone
                    noneLabel="Χωρίς πελάτη"
                    testId="time-client-select"
                    disableSearch
                  />
                </div>
              </div>
              
              {/* Case selection - only for "Υπόθεση" type and when client is selected */}
              {trackingType === 'case' && selectedClientId && (
                <div>
                  <Label className="text-sm text-slate-600">Επιλογή υπόθεσης</Label>
                  <Select
                    value={selectedCaseId || undefined}
                    onValueChange={(val) => setSelectedCaseId(val === '_none_' ? '' : val)}
                  >
                    <SelectTrigger className="w-full mt-1.5 h-10 border border-stone-300 rounded-md text-sm text-slate-700 bg-white" data-testid="time-case-select">
                      <SelectValue placeholder="Επιλέξτε υπόθεση" />
                    </SelectTrigger>
                    <SelectContent>
                      {cases.filter(c => c.client_id === selectedClientId).map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {formatCaseName(c, entries)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label className="text-sm text-slate-600">Περιγραφή</Label>
                <Input
                  data-testid="time-description-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="π.χ. Σύνταξη γνωμοδότησης"
                  className="mt-1.5 h-10 bg-white border-stone-300 text-sm placeholder:text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Hourly Rate (optional) */}
        {trackingType && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-stone-200">
              <h3 className="flex items-center gap-2.5 font-semibold text-slate-900 tracking-tight">Τιμή χρέωσης</h3>
              <p className="text-sm text-slate-500">Προαιρετικό - μπορείτε να το συμπληρώσετε αργότερα</p>
            </div>
            <div className="p-4">
              <Input
                type="number"
                step="0.01"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                placeholder="€ ανά ώρα"
                className="h-10 bg-white border-stone-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
          </div>
        )}

        {/* Start Button */}
        {trackingType && (
          <button
            data-testid="time-start-btn"
            onClick={startTracking}
            disabled={
              (trackingType === 'case' && !selectedCaseId) ||
              (trackingType === 'extrajudicial' && !description)
            }
            className="w-full flex items-center justify-center gap-2 h-12 bg-red-700 hover:bg-red-800 disabled:bg-slate-300 disabled:cursor-not-allowed disabled:shadow-none text-white font-medium rounded-xl shadow-lg shadow-red-900/20 transition-all duration-200"
          >
            <Play className="w-5 h-5" />
            Έναρξη χρονοχρέωσης
          </button>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        onClose();
      }
    }}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden [&>button]:hidden sm:rounded-2xl">
        <DialogHeader className="p-4 border-b border-stone-200 bg-white">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2.5 text-base sm:text-lg font-semibold">
              <span className="hidden sm:flex items-center justify-center w-8 h-8 rounded-lg bg-red-700/10 text-red-700 shrink-0">
                <Clock className="w-4 h-4" />
              </span>
              <span>Χρονοχρέωση</span>
              {isTracking && (
              <span className="ml-auto text-xs font-normal text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-100">
                Ενεργή
              </span>
            )}
          </DialogTitle>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all bg-stone-100 text-slate-600 hover:bg-slate-200"
          >
            Κλείσιμο
          </button>
          </div>
        </DialogHeader>
        <div className="p-4 bg-stone-50 max-h-[70vh] overflow-y-auto">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TimeTrackerModal;
